<script setup lang="ts">
	import { computed, ref, watch } from 'vue'

	const props = withDefaults(
		defineProps<{
			modelValue: number
			id: string
			label?: string
			disabled?: boolean
			compact?: boolean
		}>(),
		{ label: 'Radio de búsqueda' }
	)
	const emit = defineEmits<{
		(event: 'update:modelValue', value: number): void
	}>()
	const feedback = ref<{ text: string; radius?: number }>()
	const message = computed(() => feedback.value?.text ?? '')
	watch(
		() => props.modelValue,
		(value) => {
			if (feedback.value && feedback.value.radius !== value)
				feedback.value = undefined
		}
	)
	const update = (event: Event) => {
		const input = event.target as HTMLInputElement
		if (!input.value) {
			feedback.value = { text: 'Escribe un radio de 1 a 50 km.' }
			return
		}
		const value = Number(input.value)
		if (!Number.isFinite(value)) return
		const adjusted = Math.min(50, Math.max(1, Math.round(value)))
		feedback.value =
			adjusted !== value ?
				{
					text: `Radio ajustado a ${adjusted} km. Elige un valor entero de 1 a 50.`,
					radius: adjusted,
				}
			:	undefined
		input.value = String(adjusted)
		emit('update:modelValue', adjusted)
	}
	const restore = (event: FocusEvent) => {
		;(event.target as HTMLInputElement).value = String(props.modelValue)
		if (feedback.value && feedback.value.radius === undefined)
			feedback.value = {
				text: `Se conserva el radio de ${props.modelValue} km.`,
				radius: props.modelValue,
			}
	}
</script>

<template>
	<div
		class="min-w-0"
		:class="compact ? 'space-y-1' : 'space-y-2'"
	>
		<div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
			<label
				:for="id"
				class="text-sm font-medium text-gray-700"
			>
				{{ label }} (km)
			</label>
			<span
				v-if="compact"
				:id="`${id}-help`"
				class="text-xs text-gray-500"
			>
				1–50 km
			</span>
		</div>
		<div
			class="flex items-center"
			:class="compact ? 'gap-3' : 'gap-4'"
		>
			<input
				:id="id"
				type="range"
				min="1"
				max="50"
				step="1"
				:value="modelValue"
				:disabled="disabled"
				:aria-label="`${label} en kilómetros`"
				:aria-describedby="`${id}-help ${id}-feedback`"
				class="min-w-0 flex-1 cursor-pointer accent-[#253C70] disabled:opacity-50"
				:class="compact ? 'h-9' : 'h-6'"
				@input="update"
			/>
			<input
				:id="`${id}-exact`"
				type="number"
				inputmode="numeric"
				min="1"
				max="50"
				step="1"
				:value="modelValue"
				:disabled="disabled"
				:aria-label="`${label}: valor exacto en kilómetros`"
				:aria-describedby="`${id}-help ${id}-feedback`"
				class="rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
				:class="compact ? 'h-9 w-[4.5rem]' : 'w-20'"
				@input="update"
				@blur="restore"
			/>
		</div>
		<div
			v-if="!compact"
			:id="`${id}-help`"
			class="flex justify-between text-xs text-gray-500"
		>
			<span>1 km</span><span>Máximo 50 km</span>
		</div>
		<p
			:id="`${id}-feedback`"
			:class="message ? 'text-xs text-gray-600' : 'sr-only'"
			aria-live="polite"
		>
			{{ message }}
		</p>
	</div>
</template>
